import { Injectable } from '@angular/core';
import { User } from '../models/user';
import { HttpService } from './http.service';
import { environment } from 'src/environments/environment';
import { ApolloClient } from '@apollo/client';
import {
  SigninGQL,
  MeGQL,
  MeQuery,
  RefreshGQL,
  SignoutGQL,
  RegisterTokenValidateGQL,
  RegisterUserGQL,
  UsersGQL,
  UsersQuery,
  MeWithTelegramQuery,
  MeWithTelegramGQL,
} from 'src/gql/generated';
import { ErrorCode } from '../models/error-code.enum';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { FUNC_KEYS } from 'src/app/constants/user-functions';
import { gqlErrors } from 'src/app/utils/gql-errors';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly USER_STORE_KEY = 'user_key';

  private user: User;
  private logined: boolean;

  constructor(
    private router: Router,
    private http: HttpService,
    private signinGQL: SigninGQL,
    private meGQL: MeGQL,
    private refreshGQL: RefreshGQL,
    private signoutGQL: SignoutGQL,
    private registerTokenValidateGQL: RegisterTokenValidateGQL,
    private registerUserGQL: RegisterUserGQL,
    private usersGQL: UsersGQL,
    private meWithTelegramGQL: MeWithTelegramGQL,
  ) {
    // 形の壊れた値をログイン済みと誤認すると、ログイン画面に飛ばないまま
    // 原因の分からないエラーだけが出る状態になる。復元できなければ値ごと捨てる
    const restoreUser = (storage: Storage): boolean => {
      const obj = storage.getItem(this.USER_STORE_KEY);
      if (!obj) {
        return false;
      }
      let user: User;
      try {
        user = JSON.parse(obj);
      } catch {
        user = null;
      }
      if (!user || !user.email || !user.token) {
        storage.removeItem(this.USER_STORE_KEY);
        return false;
      }
      this.user = user;
      this.user.storeTime = new Date(this.user.storeTime);
      this.logined = true;
      return true;
    };
    if (!restoreUser(localStorage)) {
      restoreUser(sessionStorage);
    }
  }

  public registerCheck(token: string, success: any, error?: any) {
    this.registerTokenValidateGQL.fetch({ variables: { token } }).subscribe(
      (resp) => {
        if (gqlErrors(resp)) {
          if (this.http.hasExtensions(gqlErrors(resp)) && error) {
            error(gqlErrors(resp)[0].extensions);
          } else {
            this.http.handleError();
          }
        } else {
          success(resp);
        }
      },
      (err) => {
        error(err);
      },
    );
  }

  public register(password: string, salt: string, token: string, success: any, error?: any) {
    const body = { password, salt, token };
    this.registerUserGQL.mutate({ variables: body }).subscribe((resp) => {
      if (gqlErrors(resp)) {
        if (this.http.hasExtensions(gqlErrors(resp)) && error) {
          error(gqlErrors(resp)[0].extensions);
        } else {
          this.http.handleError();
        }
      } else {
        success(resp.data.registerUser);
      }
    });
  }

  public async login(email: string, password: string, isRemember: boolean, success: any, error?: any) {
    const body = { email, password, serviceType: 'nono' };
    const resp = await firstValueFrom(this.signinGQL.mutate({ variables: body }));
    if (gqlErrors(resp)) {
      if (this.http.hasExtensions(gqlErrors(resp)) && error) {
        error(gqlErrors(resp)[0].extensions);
      } else {
        this.http.handleError();
      }
    } else {
      await this.loginProcess(email, resp.data.signin.token, isRemember);
      success(resp);
    }
  }

  public async loginProcess(email: string, token: string, isRemember: boolean) {
    this.user = new User();
    this.user.email = email;
    this.user.token = token;
    this.user.storeTime = new Date();
    this.logined = true;
    if (isRemember) {
      this.user.storeType = 'local';
    } else {
      this.user.storeType = 'session';
    }
    const me = await firstValueFrom(this.meGQL.fetch());
    this.handleMeResp(me, () => {});
    this.saveUser();
  }

  public logout(success: any, error?: any) {
    this.signoutGQL.mutate().subscribe((resp) => {
      if (gqlErrors(resp)) {
        // logoutでエラーが起きたら、強制的にログアウトする
        this.forceLogout(gqlErrors(resp), error);
      } else {
        this.logined = false;
        localStorage.removeItem(this.USER_STORE_KEY);
        sessionStorage.removeItem(this.USER_STORE_KEY);
        success(resp.data);
      }
    });
  }

  public forceLogout(err: any, error?: any) {
    this.logined = false;
    localStorage.removeItem(this.USER_STORE_KEY);
    sessionStorage.removeItem(this.USER_STORE_KEY);
    if (error) {
      error(err);
    } else {
      this.http.handleError(err);
    }
  }

  public async refresh(operationName: string): Promise<string> {
    if (!this.user) {
      // 未ログインは何もしない
      return null;
    }
    if (operationName === 'refresh') {
      // すでにrefresh api requestだったら何もしない
      return null;
    }
    if (operationName === 'signout') {
      // logout api requestだったら何もしない
      return null;
    }
    const st = new Date(this.user.storeTime.getTime());
    st.setMinutes(st.getMinutes() + environment.tokenRefreshMin);
    const needRefresh = st.getTime() < new Date().getTime();
    if (!needRefresh) {
      return null;
    }
    await this.doRefresh();

    return null;
  }

  public me(success: any, error?: any): void {
    this.meGQL.fetch().subscribe((resp) => {
      this.handleMeResp(resp, success, error);
    });
  }

  public isLogined(): boolean {
    return this.logined;
  }

  public getUserToken(): string {
    if (this.user) {
      return this.user.token;
    }
    return null;
  }

  public getEmail(): string {
    if (this.user) {
      return this.user.email;
    }
    return null;
  }

  public getName(): string {
    if (this.user) {
      return this.user.name;
    }
    return null;
  }

  public isStoreManager(): boolean {
    if (this.user) {
      return this.user.isStoreManager;
    }
    return false;
  }

  public isAdmin(): boolean {
    if (this.user) {
      return this.user.userRole === 'admin';
    }
    return false;
  }

  public hasSubRole(name: string): boolean {
    return this.user?.userSubRoles?.includes(name);
  }

  public async getAllUsers(success: (resp: UsersQuery) => void, error?) {
    const serviceType = 'nono';
    const resp = await this.usersGQL.fetch({ variables: { serviceType } }).toPromise();
    if (gqlErrors(resp)) {
      if (this.http.hasExtensions(gqlErrors(resp)) && error) {
        error(gqlErrors(resp)[0].extensions);
      } else {
        this.http.handleError();
      }
    } else {
      success(resp.data);
    }
  }

  public async getUserTelegram(success: (resp: MeWithTelegramQuery) => void, error?) {
    const resp = await this.meWithTelegramGQL.fetch().toPromise();
    if (gqlErrors(resp)) {
      if (this.http.hasExtensions(gqlErrors(resp)) && error) {
        error(gqlErrors(resp)[0].extensions);
      } else {
        this.http.handleError();
      }
    } else {
      success(resp.data);
    }
  }

  public hasFuncRakuten(): boolean {
    return this.hasFuncShow(FUNC_KEYS['rakuten']);
  }

  public rakutenRequired(): void {
    this.funcRequired([FUNC_KEYS['rakuten']]);
  }

  public hasFuncShare(): boolean {
    return this.hasFuncShow(FUNC_KEYS['share']);
  }

  public hasRefreshToken(): boolean {
    if (this.user) {
      return this.user.hasRefreshToken !== false;
    }
    return true;
  }

  public shareRequired(): void {
    this.funcRequired([FUNC_KEYS['share']]);
  }

  public hasFuncAmazonNotify(): boolean {
    // 商品共有があればAmazon通知も使える
    return this.hasFuncShow(FUNC_KEYS['share']);
  }

  public amazonNotifyRequired(): void {
    // 商品共有があればAmazon通知も使える
    this.funcRequired([FUNC_KEYS['share']]);
  }

  public hasFuncBiccamera(): boolean {
    return this.hasFuncShow(FUNC_KEYS['biccamera']);
  }

  public biccameraRequired(): void {
    this.funcRequired([FUNC_KEYS['biccamera']]);
  }

  public hasFuncKojima(): boolean {
    return this.hasFuncShow(FUNC_KEYS['kojima']);
  }

  public kojimaRequired(): void {
    this.funcRequired([FUNC_KEYS['kojima']]);
  }

  public hasFuncIris(): boolean {
    return this.hasFuncShow(FUNC_KEYS['iris']);
  }

  public irisRequired(): void {
    this.funcRequired([FUNC_KEYS['iris']]);
  }

  public hasFuncSpreadSheet(): boolean {
    return this.hasFuncShow(FUNC_KEYS['spreadsheet']);
  }

  public spreadsheetRequired(): void {
    this.funcRequired([FUNC_KEYS['spreadsheet']]);
  }

  public hasFuncDonki(): boolean {
    return this.hasFuncShow(FUNC_KEYS['donki']);
  }

  public donkiRequired(): void {
    this.funcRequired([FUNC_KEYS['donki']]);
  }

  public hasFuncDiscontinue(): boolean {
    return this.hasFuncShow(FUNC_KEYS['discontinue']);
  }

  public discontinueRequired(): void {
    this.funcRequired([FUNC_KEYS['discontinue']]);
  }

  public hasFuncOpenClose(): boolean {
    return this.hasFuncShow(FUNC_KEYS['openClose']);
  }

  public openCloseRequired(): void {
    this.funcRequired([FUNC_KEYS['openClose']]);
  }

  public hasFuncAeon(): boolean {
    return this.hasFuncShow(FUNC_KEYS['aeon']);
  }

  public aeonRequired(): void {
    this.funcRequired([FUNC_KEYS['aeon']]);
  }

  public hasFuncUser(): boolean {
    return this.hasFuncShow(FUNC_KEYS['user']);
  }

  public userRequired(): void {
    this.funcRequired([FUNC_KEYS['user']]);
  }

  public hasFuncAmazonPriceTracking(): boolean {
    return this.hasFuncShow(FUNC_KEYS['amazonPriceTracking']);
  }

  public amazonPriceTrackingRequired(): void {
    this.funcRequired([FUNC_KEYS['amazonPriceTracking']]);
  }

  public hasFuncKeepa(): boolean {
    return this.hasFuncShow(FUNC_KEYS['keepa']);
  }

  public keepaRequired(): void {
    this.funcRequired([FUNC_KEYS['keepa']]);
  }

  // --------------------- private ---------------------
  private handleMeResp(resp: ApolloClient.QueryResult<MeQuery>, success: any, error?: any) {
    if (gqlErrors(resp)) {
      if (this.http.hasExtensions(gqlErrors(resp)) && error) {
        error(gqlErrors(resp)[0].extensions);
      } else {
        this.http.handleError();
      }
    } else {
      const me = resp.data.me;
      this.user.name = me.name;
      this.user.userRole = me.userRole;
      this.user.isStoreManager = me.isStoreManager;
      this.user.userSubRoles = me.userSubRoles;
      this.user.functions = me.functions;
      this.user.hasRefreshToken = me.hasRefreshToken;
      success(me);
    }
  }

  private async doRefresh() {
    const resp = await firstValueFrom(this.refreshGQL.mutate());

    if (gqlErrors(resp)) {
      if (this.http.hasExtensions(gqlErrors(resp))) {
        const code = gqlErrors(resp)[0].extensions.code;
        if (typeof code === 'string') {
          return code;
        }
      }
      return ErrorCode.InternalServerError;
    }
    this.saveUser();
  }

  private saveUser() {
    this.user.storeTime = new Date();
    if (this.user.storeType === 'local') {
      localStorage.setItem(this.USER_STORE_KEY, JSON.stringify(this.user));
    } else {
      sessionStorage.setItem(this.USER_STORE_KEY, JSON.stringify(this.user));
    }
  }

  private hasFuncShow(func: string): boolean {
    return this.user?.functions?.includes(func);
  }

  private funcRequired(funcs: string[]): void {
    const found = funcs.find((func) => this.hasFuncShow(func));
    if (found) {
      return; // いずれかの権限があればOK
    }
    console.log('go to login');
    this.router.navigate(['/login']);
  }
}
